<script lang="ts" setup>
import { AanvragenDomainEntitiesMutatiesoort as Mutatiesoort } from '@services/aanvragen-public/orval-api/model/aanvragenDomainEntitiesMutatiesoort';
import { type AanvragenMuterenRoyementDto } from '@services/aanvragen-public/orval-api/model/aanvragenMuterenRoyementDto';
import { type SUREbusinessCommonDomainSoortVerzekeringEnum as SoortVerzekering } from '@services/aanvragen-public/orval-api/model/sUREbusinessCommonDomainSoortVerzekeringEnum';
import { useMutatieRedenenGet } from '@services/aanvragen-public/orval-api/mutatie-redenen-api/mutatie-redenen-api';
import ExpandTransition from '@shared/animation/transitions/ExpandTransition/ExpandTransition.vue';
import VInfoBox from '@shared/component/VInfoBox/VInfoBox.vue';
import VModal from '@shared/component/VModal/VModal.vue';
import VSkeleton from '@shared/component/VSkeleton/VSkeleton.vue';
import FieldRowGroup from '@shared/form/FieldRowV2/FieldRowGroup.vue';
import FieldRowV2 from '@shared/form/FieldRowV2/FieldRowV2.vue';
import SelectField from '@shared/form/SelectField/SelectField.vue';
import TextField from '@shared/form/TextField/TextField.vue';
import VDatePicker from '@shared/form/VDatePicker/VDatePicker.vue';
import { parseDateOnlyAsLocalTime } from '@tools/dates';
import { logger } from '@logging/logging';
import { toTypedSchema } from '@vee-validate/zod';
import { useForm } from 'vee-validate';
import { computed, nextTick, ref, toRefs, watch } from 'vue';
import { useId } from '@tools/composables/id';
import { z } from 'zod';
import { usePakketpolisStore } from '../pakketpolisStore';

const props = defineProps<{
  pakketpolisnummer: string;
  polisnummer: string;
  soortVerzekering: SoortVerzekering;
  royeerInformatie: AanvragenMuterenRoyementDto;
}>();

const isOpen = defineModel<boolean>('isOpen');
const royementdatumId = useId('subpolis-royementdatum');

const { pakketpolisnummer, polisnummer, soortVerzekering } = toRefs(props);
const extraToelichtingNodigBijRoyementVoorDatum = computed(() =>
  parseDateOnlyAsLocalTime(props.royeerInformatie.extraToelichtingNodigBijRoyementVoorDatum),
);
const royementAlleenMogelijkVanafDatum = computed(() =>
  parseDateOnlyAsLocalTime(props.royeerInformatie.alleenMogelijkVanafDatum),
);

const pakketpolisStore = usePakketpolisStore();

const { data: mutatieredenen, isPending } = useMutatieRedenenGet(
  computed(() => ({
    mutatiesoort: Mutatiesoort.Royement,
    soortVerzekering: soortVerzekering.value,
  })),
  {
    query: {
      select: (res) => res.result,
    },
  },
);

const validationSchema = toTypedSchema(
  z
    .object({
      gekozenReden: z.string().min(1),
      redenAnders: z
        .string()
        .max(40, 'De royementsreden mag maximaal 40 karakters bevatten')
        .optional(),
      gekozenDatum: z.date(),
      redenRoyeerdatum: z.string().optional(),
    })
    .superRefine((input, ctx) => {
      const isRoyementsredenValid =
        input.gekozenReden !== 'anders' ||
        (!!input.redenAnders && !!input.redenAnders.trim().length);
      if (!isRoyementsredenValid) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'De royementsreden is verplicht.',
          path: ['redenAnders'],
        });
      }

      const isRedenRoyeerdatumValid =
        extraToelichtingNodigBijRoyementVoorDatum.value === undefined ||
        input.gekozenDatum >= extraToelichtingNodigBijRoyementVoorDatum.value ||
        (input.redenRoyeerdatum && input.redenRoyeerdatum.trim().length >= 10);
      if (!isRedenRoyeerdatumValid) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'De toelichting moet tenminste 10 tekens bevatten.',
          path: ['redenRoyeerdatum'],
        });
      }
    }),
);

const { errors, useFieldModel, validate, resetForm } = useForm({
  validationSchema,
  initialValues: {
    gekozenDatum: new Date(),
  },
});

const gekozenReden = useFieldModel('gekozenReden');
const gekozenDatum = useFieldModel('gekozenDatum');
const redenAnders = useFieldModel('redenAnders');
const redenRoyeerdatum = useFieldModel('redenRoyeerdatum');

const redenAndersField = ref<InstanceType<typeof TextField>>();
const redenRoyeerdatumField = ref<InstanceType<typeof TextField>>();

watch(gekozenReden, (e) =>
  nextTick(() => {
    if (e === 'anders') redenAndersField.value?.focus();
  }),
);

const extraToelichtingNodig = computed(
  () =>
    !!extraToelichtingNodigBijRoyementVoorDatum.value &&
    !!gekozenDatum.value &&
    gekozenDatum.value < extraToelichtingNodigBijRoyementVoorDatum.value,
);

watch(extraToelichtingNodig, (newValue) => {
  if (newValue) {
    nextTick(() => redenRoyeerdatumField.value?.focus());
  }
});

watch(
  royementAlleenMogelijkVanafDatum,
  (newValue) => {
    gekozenDatum.value = newValue ?? new Date();
  },
  { immediate: true },
);

const options = computed(() =>
  mutatieredenen.value?.map((x) => ({
    label: x.omschrijving + (x.interneOptie ? ' (Interne optie)' : ''),
    value: x.omschrijving,
  })),
);

async function save() {
  const validationResult = await validate();
  if (!validationResult.valid) {
    logger.debug('validationResult', validationResult.results);
    return;
  }

  pakketpolisStore.setSubpolisGeroyeerd(
    pakketpolisnummer.value,
    polisnummer.value,
    gekozenDatum.value!,
    gekozenReden.value === 'anders' ? redenAnders.value! : gekozenReden.value!,
    extraToelichtingNodig.value ? redenRoyeerdatum.value : undefined,
  );

  isOpen.value = false;
  resetForm();
}

watch(polisnummer, () => resetForm());
</script>
<template>
  <VModal
    overflow
    confirm-button-text="Royement opslaan"
    @close-modal="isOpen = false"
    @confirm-action="save()"
    invert-header
    :title="`Royement ${polisnummer}`"
    :is-active="isOpen"
    data-testid="sub-polis-royeer-modal"
  >
    <FieldRowGroup :gap="2">
      <FieldRowV2 label="Reden royement" :errors="[errors.gekozenReden]">
        <VSkeleton height="lg" v-if="isPending" />
        <SelectField
          v-model:value="gekozenReden"
          v-else
          :options="[...options, { label: 'Andere reden...', value: 'anders' }]"
        />
      </FieldRowV2>
      <FieldRowV2 :errors="gekozenReden === 'anders' ? [errors.redenAnders] : []">
        <ExpandTransition>
          <TextField
            v-if="gekozenReden === 'anders'"
            :rows="2"
            :max-length="40"
            v-model:value="redenAnders"
            ref="redenAndersField"
            placeholder="Geef een reden op voor het royement"
          />
        </ExpandTransition>
      </FieldRowV2>
      <div class="flex flex-col">
        <VInfoBox
          type="info"
          badge-text="belangrijk"
          v-if="royementAlleenMogelijkVanafDatum !== undefined"
          class="mt-2"
          data-testid="toekomstpolis-melding"
        >
          De polis is nog niet ingegaan. Royementen kunnen alleen op of na de ingangsdatum
          plaatsvinden.
        </VInfoBox>
        <FieldRowV2
          label="Datum royement"
          :label-for="royementdatumId"
          :errors="[errors.gekozenDatum]"
        >
          <VDatePicker
            :id="royementdatumId"
            v-model="gekozenDatum"
            :invalid="!!errors?.gekozenDatum"
            :clearable="false"
            :min-date="royementAlleenMogelijkVanafDatum"
            data-testid="royementsdatum"
          />
        </FieldRowV2>
      </div>
      <FieldRowV2 :errors="extraToelichtingNodig ? [errors.redenRoyeerdatum] : []">
        <ExpandTransition>
          <div v-if="extraToelichtingNodig" class="flex flex-col gap-4 !duration-400">
            <VInfoBox type="warning" badge-text="Extra toelichting nodig">
              We nemen alleen royementen in behandeling tot
              <span class="font-bold">14 dagen</span> in het verleden. Wil je hieronder zo
              uitgebreid mogelijk aangeven waarom dit royement verder in het verleden dient te
              worden uitgevoerd?
            </VInfoBox>
            <TextField
              :rows="6"
              v-model:value="redenRoyeerdatum"
              ref="redenRoyeerdatumField"
              placeholder="Toelichting bij royement."
            />
          </div>
        </ExpandTransition>
      </FieldRowV2>
    </FieldRowGroup>
  </VModal>
</template>
